import { useTranslation } from 'react-i18next'
import { CalendarClock, Loader2, Trash2, Undo2 } from 'lucide-react'
import Modal from '../ui/Modal'

export type DeleteDraftChoice = 'discard' | 'regenerate'

/**
 * Qué hacer con el origen de un borrador al borrarlo.
 *
 * ☠️ Borrar el borrador de una cuota o de un hito vencido no lo descarta: el plan lo
 * devuelve a Facturación → Pendientes (hasta el 26-10-2026 lo regeneraba el job de
 * las 03:00; ya no genera borradores, pero el origen sigue ahí). Por eso el borrado de
 * un borrador que viene del plan de facturación NO es un «¿seguro?» de sí/no: hay
 * que decir qué se quiere. Dos salidas, las dos con su consecuencia escrita:
 *
 *  - descartar: el hito sale del plan / la cuota se salta; no vuelve.
 *  - borrar y devolverlo a Pendientes: lo de siempre; el origen sigue pendiente.
 *
 * No hay una opción marcada de antemano: elegir la que borra datos del plan por
 * defecto, o la que lo regenera, sería decidir por quien tiene el ratón.
 */
export default function DeleteDraftModal({
  open,
  onClose,
  onChoose,
  origin,
  busy,
}: {
  open: boolean
  onClose: () => void
  onChoose: (choice: DeleteDraftChoice) => void
  origin: { recurringName: string | null; milestoneTitles: string[] } | null
  busy?: boolean
}) {
  const { t } = useTranslation('billing')
  const isMilestone = (origin?.milestoneTitles.length ?? 0) > 0
  const kind = isMilestone ? 'milestone' : 'recurring'

  return (
    <Modal open={open} onClose={onClose} title={t('deleteDraftModal.title')} maxWidth="max-w-lg">
      <div className="space-y-4">
        <p className="text-sm t-secondary">
          {isMilestone
            ? t('deleteDraftModal.introMilestone', { items: origin?.milestoneTitles.join(' · ') })
            : t('deleteDraftModal.introRecurring', { name: origin?.recurringName ?? '' })}
        </p>
        <p className="text-xs t-muted">{t('deleteDraftModal.reason')}</p>

        <div className="space-y-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => onChoose('discard')}
            className="w-full text-left rounded-lg p-3 border border-red-500/40 hover:bg-red-500/10 disabled:opacity-50"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-red-600 dark:text-red-400">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              {t(`deleteDraftModal.${kind}.discard.label`)}
            </span>
            <span className="block text-xs t-muted mt-1">
              {t(`deleteDraftModal.${kind}.discard.hint`)}
            </span>
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={() => onChoose('regenerate')}
            className="w-full text-left rounded-lg p-3 border border-base hover:bg-hover disabled:opacity-50"
          >
            <span className="flex items-center gap-2 text-sm font-semibold t-primary">
              {isMilestone ? <Undo2 size={14} /> : <CalendarClock size={14} />}
              {t(`deleteDraftModal.${kind}.regenerate.label`)}
            </span>
            <span className="block text-xs t-muted mt-1">
              {t(`deleteDraftModal.${kind}.regenerate.hint`)}
            </span>
          </button>
        </div>

        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
